/**
 * WBSBOQLink.jsx  —  v44 architecture
 *
 * A WBS activity now carries a single boq_item_id (not a many-to-many link table).
 * This component lets the user pick one BOQ item from the project's BOQ list,
 * link/unlink it, and see the current rollup-derived executed_qty.
 */
import { useCallback, useEffect, useState } from 'react';
import { Link2, Unlink, Search, Package, CheckCircle2, X, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { apiGet, apiPost, apiDel } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

// ─── Modal ────────────────────────────────────────────────────────────────────

export function WBSBOQLinkModal({ wbsTask, projectId, onClose, onSaved }) {
  const [items, setItems]         = useState([]);
  const [search, setSearch]       = useState('');
  const [selected, setSelected]   = useState(wbsTask?.boq_item_id || null);
  const [loading, setLoading]     = useState(false);
  const [saving, setSaving]       = useState(false);

  const fetchItems = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const data = await apiGet(`/wbs/available-boq?project_id=${projectId}`);
      setItems(data.items || []);
    } catch {
      toast.error('Failed to load BOQ items');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  const handleLink = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await apiPost(`/wbs/${wbsTask.id}/link-boq`, { boq_item_id: selected });
      toast.success('BOQ item linked — executed qty will roll up from this WBS activity');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Link failed');
    } finally { setSaving(false); }
  };

  const handleUnlink = async () => {
    setSaving(true);
    try {
      await apiDel(`/wbs/${wbsTask.id}/unlink-boq`);
      toast.success('BOQ link removed');
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Unlink failed');
    } finally { setSaving(false); }
  };

  const filtered = items.filter(it =>
    !search ||
    (it.description || '').toLowerCase().includes(search.toLowerCase()) ||
    (it.unit || '').toLowerCase().includes(search.toLowerCase())
  );

  const current = items.find(it => it.id === wbsTask?.boq_item_id);

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <div className="font-display font-bold text-sm text-slate-900">Link BOQ Item</div>
            <div className="text-xs text-slate-500 mt-0.5">{wbsTask?.name}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>

        {/* Current link */}
        {current && (
          <div className="mx-5 mt-4 p-3 bg-emerald-50 border border-emerald-200 rounded-lg flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-emerald-800 min-w-0">
              <CheckCircle2 size={15} className="text-emerald-600 shrink-0" />
              <span className="font-medium truncate">{current.description}</span>
              <span className="text-xs text-emerald-600 shrink-0">{current.unit}</span>
            </div>
            <button
              onClick={handleUnlink}
              disabled={saving}
              className="shrink-0 flex items-center gap-1 text-xs text-rose-600 hover:text-rose-800 font-medium"
              data-testid="wbs-boq-unlink-btn"
            >
              <Unlink size={13} /> Unlink
            </button>
          </div>
        )}

        {/* Search + list */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-3">
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search BOQ items…"
              className="w-full pl-8 pr-3 py-2 text-sm border border-slate-200 rounded-lg outline-none focus:border-[#3b4872]"
              data-testid="wbs-boq-search"
            />
          </div>

          {loading ? (
            <div className="text-center py-6 text-sm text-slate-400">Loading BOQ items…</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-6 text-sm text-slate-400">No items match your search</div>
          ) : (
            <div className="space-y-1.5">
              {filtered.map(it => (
                <button
                  key={it.id}
                  onClick={() => setSelected(it.id === selected ? null : it.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    selected === it.id
                      ? 'border-[#3b4872] bg-indigo-50'
                      : 'border-slate-100 hover:border-slate-300 bg-white'
                  }`}
                  data-testid={`wbs-boq-item-${it.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs font-semibold text-slate-800 truncate">{it.description}</div>
                      <div className="text-[11px] text-slate-500 mt-0.5">
                        {it.unit} · qty {it.quantity?.toLocaleString('en-IN')} · ₹{it.rate?.toLocaleString('en-IN')}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {it.is_linked && (
                        <span className="text-[10px] uppercase tracking-wider text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-sm">
                          Linked
                        </span>
                      )}
                      {selected === it.id && <CheckCircle2 size={16} className="text-[#3b4872]" />}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-2">
          <div className="text-xs text-slate-400 font-display">
            v44 · one BOQ link per WBS activity · executed qty rolls up automatically
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-1.5 text-xs border border-slate-200 rounded-lg font-medium text-slate-600 hover:bg-slate-50">
              Cancel
            </button>
            <button
              onClick={handleLink}
              disabled={!selected || saving}
              className="px-3 py-1.5 text-xs bg-[#3b4872] text-white rounded-lg font-medium disabled:opacity-40 flex items-center gap-1"
              data-testid="wbs-boq-link-save-btn"
            >
              {saving ? <RefreshCw size={12} className="animate-spin" /> : <Link2 size={12} />}
              Link to BOQ
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


// ─── Coverage panel ───────────────────────────────────────────────────────────

export function WBSBOQCoverage({ projectId }) {
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const d = await apiGet(`/wbs/boq-coverage?project_id=${projectId}`);
      setData(d);
    } catch {
      // Silent failure — coverage is optional analytics
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading || !data) return null;

  return (
    <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-100 rounded-lg text-xs">
      <Package size={13} className="text-slate-400 shrink-0" />
      <div className="flex items-center gap-4 flex-wrap">
        <div>
          <span className="font-bold text-slate-700" data-testid="wbs-boq-coverage-pct">
            {data.coverage_pct ?? 0}%
          </span>
          <span className="text-slate-500 ml-1">BOQ coverage</span>
        </div>
        <div className="text-slate-500">
          {data.covered_boq_items}/{data.total_boq_items} BOQ items have WBS activities
        </div>
        <div className="text-slate-500">
          {data.wbs_linked}/{data.wbs_total} WBS tasks are BOQ-linked
        </div>
      </div>
    </div>
  );
}


// ─── Inline link badge (used in WBS task row) ─────────────────────────────────

export function WBSBOQLinkBadge({ boqItemId, boqDescription, onLinkClick }) {
  if (!boqItemId) {
    return (
      <button
        onClick={onLinkClick}
        className="text-[10px] text-slate-400 hover:text-[#3b4872] flex items-center gap-0.5"
        data-testid="wbs-boq-link-add-btn"
      >
        <Link2 size={10} /> Link BOQ
      </button>
    );
  }
  return (
    <button
      onClick={onLinkClick}
      className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm flex items-center gap-0.5 max-w-[140px] truncate"
      data-testid="wbs-boq-linked-badge"
      title={boqDescription}
    >
      <Link2 size={9} className="shrink-0" />
      <span className="truncate">{boqDescription || 'BOQ Linked'}</span>
    </button>
  );
}
